import { jsPDF, AcroFormTextField } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import cciLogo from '../assets/cci-logo.png';
import { compressImage } from './compressImage';
import { stampPageNumbers, CCI_FOOTER } from './stampPageNumbers';
import { sanitizeFilenamePart } from './exportFilename';

// jspdf-autotable 5 no longer attaches doc.autoTable on a bare import (it only
// self-applies to a global window.jsPDF, which the ESM import never sets), so
// the plugin is applied explicitly. CONF-237.
applyPlugin(jsPDF);

const makeTextField = (doc, name, x, y, width, height, defaultValue = '', multiline = false, textAlign = 0) => {
  const field = new AcroFormTextField();
  field.fieldName = name;
  field.x = x;
  field.y = y;
  field.width = width;
  field.height = height;
  field.value = defaultValue;
  field.fontSize = 8;
  field.multiLine = multiline;
  field.textAlign = textAlign;
  doc.addField(field);
};

/**
 * Draws the CCI hardware quote onto the CURRENT page of `doc` and any pages it
 * needs to add. Does not create, stamp, or save the document — the caller owns
 * those, so this can be either a standalone PDF (generateCciQuotePDF below) or
 * one section of the combined CONF-186 document.
 */
export async function renderCciQuotePages(doc, rows, config, userName) {
  // Thresholds below were authored against US Letter (279.4mm). Deriving them
  // from the live page height lets the same code lay out on the A4 combined
  // document (CONF-186) without pushing content off the page.
  // US Letter is 279.4mm, so the offsets carry the .4: 279.4 - 12.4 == 267
  // exactly. Rounding them to 12/39/59 would shift every break point 0.4mm and
  // silently repaginate the standalone Letter PDF, since lastAutoTable.finalY
  // is a fractional value computed from font metrics.
  const pageHeight = doc.internal.pageSize.height;
  const BOTTOM_LIMIT = pageHeight - 12.4;    // was 267
  const SIGNATURE_LIMIT = pageHeight - 39.4; // was 240
  const TERMS_LIMIT = pageHeight - 59.4;     // was 220

  const NAVY = [0, 51, 102];
  const GRAY_TEXT = [100, 100, 100];
  const TC_BAND = [200, 200, 200];

  const logoData = await compressImage(cciLogo);
  doc.addImage(logoData, 'JPEG', 15, 15, 70, 21);

  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  const addrLines = [
    'CCIntegration, Inc.',
    '620 SHILOH RD',
    'STE 200',
    'PLANO, TX, 75074-7255',
    '',
    'Phone: +1.408.228.1314',
    'Web: www.ccintegration.com',
  ];
  let addrY = 40;
  addrLines.forEach((line) => {
    doc.text(line, 15, addrY);
    addrY += 4;
  });

  doc.setFillColor(...NAVY);
  doc.rect(15, 65, 90, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text('ISSUED TO:', 17, 69);

  const issuedToDefaults = [
    'Vdura, Inc',
    '1501 Reedsdale Street, Suite 400',
    'Pittsburgh PA 15233',
    'UNITED STATES OF AMERICA',
  ];
  issuedToDefaults.forEach((val, i) => {
    const f = new AcroFormTextField();
    f.fieldName = `issuedToRow${i + 1}`;
    f.x = 15;
    f.y = 71 + i * 5;
    f.width = 90;
    f.height = 5;
    f.value = val;
    f.fontSize = 8;
    f.multiLine = false;
    f.borderColor = [255, 255, 255];
    doc.addField(f);
  });

  doc.setFillColor(...NAVY);
  doc.rect(115, 15, 86, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('QUOTE', 158, 24, { align: 'center' });
  doc.setFont('helvetica', 'normal');

  const today = new Date();
  const todayStr = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
  const expStr = (() => {
    if (!config.quoteExpirationDate) return '';
    const [y, m, d] = config.quoteExpirationDate.split('-');
    return `${m}/${d}/${y}`;
  })();

  doc.setFontSize(8);

  let metaY = 33;
  const labelX = 115;
  const valueX = 201;

  doc.setTextColor(...GRAY_TEXT);
  doc.text('Quote Nbr.:', labelX, metaY + 4);
  makeTextField(doc, 'quoteNumber', 155, metaY, 46, 6, '', false, 2);

  metaY += 6;
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Order Date:', labelX, metaY + 4);
  doc.setTextColor(0, 0, 0);
  doc.text(todayStr, valueX, metaY + 4, { align: 'right' });

  metaY += 6;
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Valid Until:', labelX, metaY + 4);
  doc.setTextColor(0, 0, 0);
  doc.text(expStr, valueX, metaY + 4, { align: 'right' });

  metaY += 6;
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Sales Person:', labelX, metaY + 4);
  doc.setTextColor(0, 0, 0);
  doc.text(userName || '', valueX, metaY + 4, { align: 'right' });

  metaY += 6;
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Customer ID:', labelX, metaY + 4);
  makeTextField(doc, 'customerId', 155, metaY, 46, 6, '', false, 2);

  metaY += 6;
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Reference:', labelX, metaY + 4);

  // Pinned to 'en-US' (not the runtime locale) so this always agrees with
  // quotePdfGenerator.js's formatCurrency, which prints the lead page's
  // hardware total in the combined CONF-186 document — otherwise the same
  // dollar figure could print two different ways a page apart depending on
  // the browser's locale.
  const fmt4 = (n) =>
    Number(n).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const fmt2 = (n) =>
    Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const lineItems = rows.map((row, i) => [
    i + 1,
    `${row.cciSku}: ${row.description}`,
    row.quantity.toFixed(4),
    'EA',
    fmt4(row.unitCost),
    '0%',
    fmt2(row.totalCost),
  ]);

  // Freight line item disabled for now (per request). Re-enable by uncommenting.
  // lineItems.push([
  //   rows.length + 1,
  //   'FREIGHT-OUTBOUND: Freight - Outbound shipping from CCIntegration to recipient',
  //   '1.0000',
  //   'EA',
  //   fmt4(0),
  //   '0%',
  //   fmt2(0),
  // ]);

  const grandTotal = rows.reduce((sum, r) => sum + r.totalCost, 0);

  doc.setTextColor(0, 0, 0);
  doc.autoTable({
    startY: 97,
    head: [['NO.', 'ITEM', 'QTY.', 'UOM', 'PRICE', 'DISC.', 'AMOUNT']],
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    body: lineItems,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.5 },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 18, halign: 'right' },
      3: { cellWidth: 12, halign: 'center' },
      4: { cellWidth: 26, halign: 'right' },
      5: { cellWidth: 14, halign: 'center' },
      6: { cellWidth: 24, halign: 'right' },
    },
    margin: { left: 15, right: 15 },
  });

  // Reserve ~35% of the page for the table zone (98mm from startY=97)
  let finalY = Math.max(doc.lastAutoTable.finalY + 7, 195);

  if (finalY > SIGNATURE_LIMIT) {
    doc.addPage();
    finalY = 20;
  }

  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  // jsPDF's draw colour survives addPage(), and in the combined CONF-186
  // document a preceding section (the summary page) leaves it gray. Set it
  // explicitly rather than relying on the jsPDF default so these signature
  // rules render the same whether this is the standalone CCI PDF or a
  // section riding along in the combined document.
  doc.setDrawColor(0, 0, 0);
  doc.text('Signature:', 15, finalY);
  doc.line(33, finalY, 90, finalY);
  doc.text('Date:', 100, finalY);
  doc.line(112, finalY, 150, finalY);

  finalY += 6;
  doc.setTextColor(...GRAY_TEXT);
  const agreeLines = doc.splitTextToSize(
    'By signing this Quote you agree to all of the Terms and Conditions',
    130
  );
  doc.text(agreeLines, 15, finalY);

  const totalsStartY = finalY - 6;
  const totalsX = 201;

  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Quote Total:', totalsX - 50, totalsStartY, { align: 'left' });
  doc.setTextColor(0, 0, 0);
  doc.text(`$${fmt2(grandTotal)}`, totalsX, totalsStartY, { align: 'right' });

  doc.setTextColor(...GRAY_TEXT);
  doc.text('Less Discount:', totalsX - 50, totalsStartY + 4, { align: 'left' });
  doc.setTextColor(0, 0, 0);
  doc.text('0.00', totalsX, totalsStartY + 4, { align: 'right' });

  doc.setTextColor(...GRAY_TEXT);
  doc.text('Tax Total:', totalsX - 50, totalsStartY + 8, { align: 'left' });
  doc.setTextColor(0, 0, 0);
  doc.text('0.00', totalsX, totalsStartY + 8, { align: 'right' });

  doc.setTextColor(...GRAY_TEXT);
  doc.text('Total (USD):', totalsX - 50, totalsStartY + 12, { align: 'left' });
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text(`$${fmt2(grandTotal)}`, totalsX, totalsStartY + 12, { align: 'right' });
  doc.setFont('helvetica', 'normal');

  finalY += agreeLines.length * 4 + 8;

  if (finalY > TERMS_LIMIT) {
    doc.addPage();
    finalY = 20;
  }

  doc.setFillColor(...TC_BAND);
  doc.rect(15, finalY, 186, 6, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('TERMS AND CONDITIONS', 108, finalY + 4, { align: 'center' });
  doc.setFont('helvetica', 'normal');

  finalY += 10;

  const tcItems = [
    '1. All Pricing is quoted FOB/FCA San Jose under your existing terms. If no terms currently exist all quotes are sent under Prepaid Terms',
    '2. All pricing and availability are based on the most recent information we have, and is subject to change without notice or obligation.',
    '3. All hardware configuration is customized per each customer\'s request. Orders may not be cancelled once placed with CCI.',
    '4. For product shipped from any CCI facility, the client is responsible for ALL Shipping charges (added to order, or freight coordinated by Customer).',
    '5. This Quote is valid until the earlier of the Valid Until date as noted, or a maximum of 30 days from the Order Date above.',
    '6. All Pricing is listed in US Dollars.',
  ];

  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);

  tcItems.forEach((item) => {
    const wrapped = doc.splitTextToSize(item, 186);
    const blockHeight = wrapped.length * 4;
    if (finalY + blockHeight > BOTTOM_LIMIT) {
      doc.addPage();
      finalY = 20;
    }
    doc.text(wrapped, 15, finalY);
    finalY += blockHeight;
  });

  finalY += 2;

  const refIntro =
    'These terms are not all encompassing—for a full list of the CCI Terms and Conditions please review our Standard Terms and Conditions at:';
  const refIntroWrapped = doc.splitTextToSize(refIntro, 186);
  const refIntroHeight = refIntroWrapped.length * 4;

  if (finalY + refIntroHeight + 6 > BOTTOM_LIMIT) {
    doc.addPage();
    finalY = 20;
  }

  doc.text(refIntroWrapped, 15, finalY);
  finalY += refIntroHeight + 1;

  doc.setFont('helvetica', 'bold');
  doc.text('http://www.ccintegration.com/terms-and-conditions/', 15, finalY);
  doc.setFont('helvetica', 'normal');
}

export function buildCciFilename(config) {
  const customerName = sanitizeFilenamePart(config.preparedFor, 'Customer');
  const quoteSerial = sanitizeFilenamePart(config.quoteSerial || config.offerNumber, 'QUOTE');
  return `CCI_V5000_Quotation_${customerName}_${quoteSerial}.pdf`;
}

/**
 * Standalone CCI hardware quote — one Letter-size PDF, downloaded.
 * Retained unchanged: CONF-186 keeps the individual quotes printable, and
 * App.js falls back to this pair if the combined document fails to build.
 */
export async function generateCciQuotePDF(rows, config, userName) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  await renderCciQuotePages(doc, rows, config, userName);
  stampPageNumbers(doc, CCI_FOOTER);
  doc.save(buildCciFilename(config));
}
